import React from 'react';
import { createPortal } from 'react-dom';
import { DuplicateInfo } from '../services/inventoryService';
import '../styles/components/duplicateItemDialog.css';

export type { DuplicateInfo };

interface DuplicateItemDialogProps {
  isOpen: boolean;
  onClose: () => void;
  duplicateInfo: DuplicateInfo;
  /** Current collection ID the user is viewing (undefined if All Items) */
  currentCollectionId?: number;
  /** Current collection name for display */
  currentCollectionName?: string;
  onKeepExisting: (itemId: number) => void;
  onReplaceWithNew: (itemId: number) => void;
  onIncreaseQuantity?: (itemId: number) => void;
}

const DuplicateItemDialog: React.FC<DuplicateItemDialogProps> = ({
  isOpen,
  onClose,
  duplicateInfo,
  currentCollectionId,
  currentCollectionName,
  onKeepExisting,
  onReplaceWithNew,
  onIncreaseQuantity,
}) => {
  if (!isOpen) return null;

  const { duplicate_item_id, duplicate_item_name, duplicate_item_quantity } = duplicateInfo;

  const isInCollection = currentCollectionId != null;
  const alreadyInCurrentCollection = isInCollection && duplicateInfo.duplicate_collection_ids.some(id => Number(id) === Number(currentCollectionId));
  const willAddToCollection = isInCollection && !alreadyInCurrentCollection;

  // Build subtitle based on context
  let subtitle = `"${duplicate_item_name}" already exists.`;
  if (alreadyInCurrentCollection) {
    subtitle = `"${duplicate_item_name}" already exists in ${currentCollectionName} (qty: ${duplicate_item_quantity}).`;
  } else if (willAddToCollection) {
    subtitle = `"${duplicate_item_name}" already exists but is not in ${currentCollectionName}.`;
  }

  return createPortal(
    <div className="duplicate-dialog-overlay" onClick={onClose}>
      <div className="duplicate-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="duplicate-dialog-icon">⚠️</div>
        <div className="duplicate-dialog-title">Item Already Exists</div>
        <div className="duplicate-dialog-message">{subtitle}</div>

        <div className="duplicate-dialog-actions">
          {alreadyInCurrentCollection ? (
            <>
              {onIncreaseQuantity && (
                <button
                  className="duplicate-dialog-btn primary"
                  onClick={() => {
                    onIncreaseQuantity(duplicate_item_id);
                    onClose();
                  }}
                >
                  Increase quantity to {duplicate_item_quantity + 1}
                </button>
              )}

              <button
                className="duplicate-dialog-btn secondary"
                onClick={() => {
                  onReplaceWithNew(duplicate_item_id);
                  onClose();
                }}
              >
                Replace with new
              </button>

              <button className="duplicate-dialog-btn cancel" onClick={onClose}>
                Do nothing
              </button>
            </>
          ) : (
            <>
              <button
                className="duplicate-dialog-btn primary"
                onClick={() => {
                  onKeepExisting(duplicate_item_id);
                  onClose();
                }}
              >
                Use existing{willAddToCollection ? ` and add to ${currentCollectionName}` : ''}
              </button>

              <button
                className="duplicate-dialog-btn secondary"
                onClick={() => {
                  onReplaceWithNew(duplicate_item_id);
                  onClose();
                }}
              >
                Replace with new{willAddToCollection ? ` and add to ${currentCollectionName}` : ''}
              </button>

              <button className="duplicate-dialog-btn cancel" onClick={onClose}>
                Cancel
              </button>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default DuplicateItemDialog;
